import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Open modals, topmost last. Only the topmost one reacts to Escape and traps Tab. */
const stack: HTMLElement[] = [];

/**
 * Scrim + focus trap + Escape + focus return around a design-system <Dialog>.
 * (The DS Dialog renders the panel only; the app owns modality — guideline 70: dialogs trap focus and return it.)
 * Keyboard handling is on the document so Escape still works if focus was lost (e.g. a button disabled while busy).
 */
export function Modal({ children, onClose, top, initialFocus }: { children: ReactNode; onClose: () => void; top?: boolean; initialFocus?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const root = ref.current!;
    stack.push(root);
    const pick = () => {
      const explicit = initialFocus ? root.querySelector<HTMLElement>(initialFocus) : root.querySelector<HTMLElement>('[data-autofocus]');
      const body = root.querySelector('.ex-dialog-body, .ex-cmd');
      const first = explicit ?? body?.querySelector<HTMLElement>(FOCUSABLE) ?? root.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    };
    const t = setTimeout(pick, 0);
    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== root) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
      } else if (e.key === 'Tab') {
        const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
        if (!items.length) return;
        const first = items[0], last = items[items.length - 1];
        const inside = root.contains(document.activeElement);
        if (!inside) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      const i = stack.lastIndexOf(root);
      if (i >= 0) stack.splice(i, 1);
      if (prev && document.contains(prev)) prev.focus();
    };
  }, [initialFocus]);

  return createPortal(
    <div
      ref={ref}
      className={'ex-modal' + (top ? ' is-top' : '')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeRef.current();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
